import { useEffect } from "react";
import { useSignal } from "@preact/signals-react";
import { useNavigate } from "react-router";
import { FormattedMessage as FM } from "react-intl";

import Board from "componentsv2/board/Board";
import { layout as layoutOpportunity } from "../layoutOpportunity";
import { genStoreId } from "componentsv2/tools/functions";
import { XMarkIcon, ChartBarIcon } from "components/Icons";
import StatCard from "./StatCard";
import BarChart from "components/Chart/BarChart";
import proxy from "api/proxy";
import dates from "tools/dates";

function ViewOpportunity(props) {
  const navigate = useNavigate();
  const currentView = useSignal("list");
  const recordStoreId = useSignal(null);
  const sidebarOpen = useSignal(true);
  const stats = useSignal({ newOpps: {}, wonOpps: {}, day: 0, month: "" });
  const chartData = useSignal(null);
  const { config } = props;
  const _layoutOpportunity = layoutOpportunity(config);
  const monthName = new Date().toLocaleString('es-ES', { month: 'long' });

  async function getMonthlyStats() {
    const now = new Date();
    const currentStart = dates.fmtDate2Tryton(dates.startOfMonth(now));
    const currentEnd = dates.fmtDate2Tryton(dates.endOfMonth(now));
    const prevMonth = dates.dateAdd(now, "months", -1);
    const prevStart = dates.fmtDate2Tryton(dates.startOfMonth(prevMonth));
    const prevEnd = dates.fmtDate2Tryton(dates.endOfMonth(prevMonth));

    const [newCurr, newPrev, wonCurrData, wonPrevData] = await Promise.all([
      proxy.searchCount("crm.opportunity", [
        ["start_date", ">=", currentStart],
        ["start_date", "<=", currentEnd],
      ]),
      proxy.searchCount("crm.opportunity", [
        ["start_date", ">=", prevStart],
        ["start_date", "<=", prevEnd],
      ]),
      proxy.methodCall({
        model: "crm.opportunity",
        method: "get_won_stats",
        args: [currentStart, currentEnd],
      }),
      proxy.methodCall({
        model: "crm.opportunity",
        method: "get_won_stats",
        args: [prevStart, prevEnd],
      }),
    ]);

    const newCurrent = newCurr?.data ?? 0;
    const newPrevious = newPrev?.data ?? 0;
    const wonCurrentObj = wonCurrData?.data || {};
    const wonPrevObj = wonPrevData?.data || {};
    const wonCurrent = wonCurrentObj.count || 0;
    const wonPrevious = wonPrevObj.count || 0;
    const totalWonAmount = wonCurrentObj.total_amount || 0;

    return {
      newOpps: {
        current: newCurrent,
        previous: newPrevious,
        growth: newPrevious > 0 ? ((newCurrent - newPrevious) / newPrevious) * 100 : 0,
      },
      wonOpps: {
        current: wonCurrent,
        previous: wonPrevious,
        amount: totalWonAmount,
        growth: wonPrevious > 0
          ? ((wonCurrent - wonPrevious) / wonPrevious) * 100
          : wonCurrent > 0
            ? 100
            : 0,
      },
      day: now.getDate(),
      month: monthName,
    };
  }

  useEffect(() => {
    getMonthlyStats().then(result => {
      stats.value = result;
    });

    if (currentView.value === "column") {
      sidebarOpen.value = false;
    }
    proxy.methodCall({
      model: "crm.opportunity",
      method: "get_monthly_new_opportunities",
      args: [new Date().getFullYear()],
    }).then(res => {
      if (res?.data) {
        chartData.value = res.data;
      }
    });
  }, [currentView.value]);

  const handlereturn = () => {
    navigate(-1);
  };

  function handleView(action, recStoreId) {
    if (action === "return") {
      handlereturn();
      return;
    }
    if (["edit", "add"].includes(action)) {
      currentView.value = "form";
      recordStoreId.value =
        recStoreId || genStoreId(_layoutOpportunity.model, null);
    } else {
      currentView.value = "list";
      recordStoreId.value = null;
    }
  }

  return (
    <div className="flex max-h-[calc(100vh-7rem)] w-full flex-col">
      <div className="isolate flex flex-auto overflow-hidden bg-gray-100 md:flex w-full">
        <div className="flex-1 min-w-0 overflow-y-auto">
          <Board
            layout={_layoutOpportunity}
            returnFilter={handlereturn}
            currentView={currentView}
            showBackButton={true}
            setView={currentView}
            handleView={handleView}
            recordStoreId={recordStoreId}
          />
        </div>

        {sidebarOpen.value ? (
          <div className="hidden w-80 flex-none border-l border-gray-100 px-4 py-5 md:flex md:flex-col overflow-hidden ">
            <div className="flex-none">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    sidebarOpen.value = false;
                  }}
                  className="text-xl flex justify-around pb-3"
                >
                  <XMarkIcon className="w-7 fill-current text-neutral-500 hover:text-red-600 active:text-blue-300" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0 space-y-4">
              <StatCard
                title= <FM id="crm.opportunity.new_oportunities_title" />
                number={stats.value.newOpps?.current}
                day={stats.value.day}
                month={stats.value.month}
                growth={stats.value.newOpps?.growth}
                bgColor="bg-white"
                borderColor="border-gray-300"
              />
              <StatCard
                title= <FM id="crm.opportunity.won_month_title" />
                number={stats.value.wonOpps?.current}
                growth={stats.value.wonOpps?.growth}
                amount={stats.value.wonOpps?.amount}
                bgColor="bg-white"
                borderColor="border-gray-300"
              />
              {chartData.value && (
                <BarChart
                  name={<FM id="crm.opportunity.new_oportunities_title" />}
                  data={chartData.value}
                />
               )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-1 border-l border-gray-100 p-2 self-start mt-2 justify-center center space-y-4">
            <button
              type="button"
              onClick={() => {
                sidebarOpen.value = true;
              }}
              className="text-gray-600 fill-indigo-700 p-1.5 rounded text-xs font-semibold leading-none"
              >
                  <ChartBarIcon/>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ViewOpportunity;
